import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import {
  ArrayField,
  ArrayInput,
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  Edit,
  EditButton,
  FunctionField,
  List,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput, //
  Show,
  ShowButton,
  SimpleForm,
  SimpleFormIterator,
  SimpleShowLayout,
  TextField,
  TextInput,
  useNotify,
} from "react-admin";
import { AssetBulkActionButtons } from "./components";

const useListStyles = makeStyles({
  nowrapColumn: {
    whiteSpace: "nowrap",
  },
  centeredBoolean: {
    justifyContent: "center",
    width: "100%",
  },
  settingsColumn: {
    minWidth: 640,
    width: "100%",
  },
  settingsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  settingsNote: {
    fontWeight: "normal",
    marginLeft: 16,
    whiteSpace: "nowrap",
  },
});

const SettingsField = ({ record }) => {
  const qualityColumns = [
    { key: "high", label: "High" },
    { key: "medium", label: "Medium" },
    { key: "low", label: "Low" },
    { key: "auto", label: "Auto" },
  ];

  return (
    <div style={{ minWidth: 640 }}>
      <Table size="small" style={{ tableLayout: "fixed", border: "1px solid #000" }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: 105, border: "1px solid #000", fontWeight: "bold", textAlign: "center" }}>Platform</TableCell>
            {qualityColumns.map((quality) => (
              <TableCell key={quality.key} style={{ border: "1px solid #000", fontWeight: "bold", textAlign: "center" }}>
                {quality.label}
                <br />
                (Upper / Lower)
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {record.settings.map((setting, settingIndex) => (
            <TableRow key={`${setting.platform}-${settingIndex}`}>
              <TableCell style={{ border: "1px solid #000", fontWeight: "bold" }}>{setting.platform}</TableCell>
              {qualityColumns.map((quality) => {
                const bitrate = setting.bitrate.find((item) => item.quality === quality.key);
                const value = bitrate ? `${bitrate.bitrate_upper_limit} / ${bitrate.bitrate_lower_limit}${bitrate.is_auto ? " *" : ""}` : "-";

                return (
                  <TableCell key={quality.key} style={{ border: "1px solid #000", textAlign: "center" }}>
                    {value}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const BitrateProfileList = (props) => {
  const classes = useListStyles();
  const settingsLabel = (
    <span className={classes.settingsHeader}>
      <span>Settings</span>
      <span className={classes.settingsNote}>* : Is auto = True</span>
    </span>
  );

  return (
    <List {...props} bulkActionButtons={<AssetBulkActionButtons />}>
      <Datagrid>
        <TextField source="name" cellClassName={classes.nowrapColumn} headerClassName={classes.nowrapColumn} />
        <FunctionField label={settingsLabel} cellClassName={classes.settingsColumn} headerClassName={classes.settingsColumn} render={(record) => <SettingsField record={record} />} />
        <BooleanField source="is_default_video_setting" className={classes.centeredBoolean} cellClassName={classes.nowrapColumn} headerClassName={classes.nowrapColumn} textAlign="center" />

        <ShowButton />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const BitrateProfileTitle = ({ record }) => {
  return <span>BitrateProfile {record ? `${record.name}` : ""}</span>;
};

export const BitrateProfileShow = (props) => (
  <Show {...props} title={<BitrateProfileTitle />}>
    <SimpleShowLayout>
      <TextField source="name" />
      <ArrayField source="settings">
        <Datagrid>
          <TextField source="platform" />
          <ArrayField source="bitrate">
            <Datagrid>
              <TextField source="quality" />
              <TextField source="bitrate_upper_limit" />
              <TextField source="bitrate_lower_limit" />
              <BooleanField source="is_auto" />
            </Datagrid>
          </ArrayField>
        </Datagrid>
      </ArrayField>
      <BooleanField source="is_default_video_setting" />
    </SimpleShowLayout>
  </Show>
);

export const BitrateProfileEdit = (props) => {
  const notify = useNotify();
  const onFailure = (error) => {
    notify(error.message.toString(), "warning");
  };
  return (
    <Edit {...props} onFailure={onFailure} mutationMode="pessimistic" title={<BitrateProfileTitle />}>
      <SimpleForm>
        <TextInput source="name" validate={required()} />
        <ArrayInput source="settings">
          <SimpleFormIterator>
            <ReferenceInput label="Platform" source="platform" reference="key-value-by-platform">
              <SelectInput optionText="name" optionValue="id" />
            </ReferenceInput>

            <ArrayInput source="bitrate" label="Bitrates" validate={required()}>
              <SimpleFormIterator>
                <ReferenceInput label="Quality" source="quality" reference="key-value-by-quality">
                  <SelectInput optionText="name" optionValue="id" />
                </ReferenceInput>

                <NumberInput label="Bitrate upper limit" source="bitrate_upper_limit" validate={required()} />
                <NumberInput label="Bitrate lower limit" source="bitrate_lower_limit" validate={required()} />
                <BooleanInput label="Is Auto" source="is_auto" />
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleFormIterator>
        </ArrayInput>
        <BooleanInput label="Default Video Settings" source="is_default_video_setting" />
      </SimpleForm>
    </Edit>
  );
};

const redirect = () => "/bitrate-profiles";
export const BitrateProfileCreate = (props) => {
  const notify = useNotify();
  const onFailure = (error) => {
    notify(error.message.toString(), "warning");
  };
  return (
    <Create {...props} onFailure={onFailure}>
      <SimpleForm redirect={redirect}>
        <TextInput source="name" validate={required()} />
        <ArrayInput source="settings">
          <SimpleFormIterator>
            <ReferenceInput label="Platform" source="platform" reference="key-value-by-platform">
              <SelectInput optionText="name" optionValue="id" />
            </ReferenceInput>
            <ArrayInput source="bitrate" label="Bitrates" validate={required()}>
              <SimpleFormIterator>
                <ReferenceInput label="Quality" source="quality" reference="key-value-by-quality">
                  <SelectInput optionText="name" optionValue="id" />
                </ReferenceInput>
                <NumberInput label="Bitrate upper limit" source="bitrate_upper_limit" validate={required()} />
                <NumberInput label="Bitrate lower limit" source="bitrate_lower_limit" validate={required()} />
                <BooleanInput label="Is Auto" source="is_auto" />
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleFormIterator>
        </ArrayInput>
        <BooleanInput label="Default Video Settings" source="is_default_video_setting" />
      </SimpleForm>
    </Create>
  );
};
